import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Drawer,
  EmptyState,
  EmployeeAvatar,
  Icon,
  IconBtn,
  Input,
  PageHeader,
  Spinner,
  Tabs,
} from "../../../design-system";
import {
  getEmployees,
  getJornadaLaboral,
  getTimeTrackingSettings,
} from "../../../helpers/kronaly_helper";
import useCurrentMembership from "../../../helpers/useCurrentMembership";
import { formatTimeHHMM, todayYmdInTz, ymdToLocalDate, DEFAULT_TZ } from "../../../helpers/datetime";
import BandejaCorrecciones from "../../Jornada/Historial/BandejaCorrecciones";
import CalendarioMes from "../../Jornada/Historial/CalendarioMes";
import CierreMensualPanel from "../../Jornada/Historial/CierreMensualPanel";
import CorregirJornadaModal from "../../Jornada/Historial/CorregirJornadaModal";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const WEEKDAY_SHORT = ["lun", "mar", "mié", "jue", "vie", "sáb", "dom"];
const WEEKDAY_LONG = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const STATUS_META = {
  ok:       { label: "Correcto",       tone: "success" },
  incident: { label: "Con incidencia", tone: "warning" },
  review:   { label: "Sin marcaje",    tone: "danger" },
  current:  { label: "En curso",       tone: "info" },
};

function pad(n) { return String(n).padStart(2, "0"); }
function fmtDur(min) {
  if (min == null) return "—";
  const sign = min < 0 ? "-" : "";
  const abs = Math.abs(min);
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}
function diffStr(diff) {
  if (diff == null) return "—";
  const sign = diff >= 0 ? "+" : "−";
  const abs = Math.abs(diff);
  return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
}
function ymdToParts(ymd) {
  const [y, m, d] = ymd.split("-").map((v) => parseInt(v, 10));
  return { y, m, d };
}
function dayOfWeekIdx(ymd) {
  const d = ymdToLocalDate(ymd);
  return (d.getDay() + 6) % 7;
}
function monthLabel(ymd) { const { y, m } = ymdToParts(ymd); return `${MONTH_NAMES[m - 1]} ${y}`; }
function firstOfMonth(ymd) { const { y, m } = ymdToParts(ymd); return `${y}-${pad(m)}-01`; }
function addMonths(firstYmd, delta) {
  const { y, m } = ymdToParts(firstYmd);
  const total = (y * 12 + (m - 1)) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${pad(nm)}-01`;
}

function classifyDay(day, todayYmd, tz) {
  const planned = day.planned_minutes || 0;
  const worked = day.worked_minutes || 0;
  const segments = day.record?.segments || [];
  const breaks = day.record?.breaks || [];
  const totalBreakMin = breaks.reduce((acc, b) => {
    const start = new Date(b.start_at);
    const end = new Date(b.end_at);
    return acc + Math.max(0, Math.round((end - start) / 60000));
  }, 0);
  const firstIn = segments[0]?.clock_in_at || null;
  const lastSegment = segments.length ? segments[segments.length - 1] : null;
  const lastOut = lastSegment?.clock_out_at || null;
  const isOpen = !!(lastSegment && !lastSegment.clock_out_at);
  const isFuture = day.date > todayYmd;
  const isWeekend = planned === 0 && [5, 6].includes(dayOfWeekIdx(day.date));
  const noPlan = planned === 0 && !isWeekend;
  let status = null;
  if (isOpen) status = "current";
  else if (planned > 0 && worked === 0 && !isFuture) status = "review";
  else if (worked > 0) status = Math.abs(worked - planned) > 15 ? "incident" : "ok";
  return {
    ...day,
    weekday: WEEKDAY_SHORT[dayOfWeekIdx(day.date)],
    weekdayLong: WEEKDAY_LONG[dayOfWeekIdx(day.date)],
    dayNumber: ymdToParts(day.date).d,
    inStr: formatTimeHHMM(firstIn, tz),
    outStr: isOpen ? "—" : formatTimeHHMM(lastOut, tz),
    pauseMin: totalBreakMin,
    isFuture, isWeekend, noPlan, isOpen,
    status, balance: worked - planned, firstIn, lastOut,
  };
}

/**
 * Vista administrativa de jornadas. Vive en RRHH. Permite a un manager/owner:
 *  - Consultar el historial de cualquier empleado del workspace.
 *  - Corregir jornadas de otros (on_behalf, requiere aprobación del afectado).
 *  - Aprobar/rechazar correcciones pendientes de cualquier empleado.
 *  - Cerrar y reabrir meses.
 *
 * Es distinta de `/jornada/historial`, que es la vista personal del propio
 * usuario (solo sus jornadas y sus solicitudes).
 */
function RRHHJornadas() {
  document.title = "Jornadas | RRHH | Kronaly";
  const membership = useCurrentMembership();
  const canApprove = membership.can("time_tracking_corrections:approve");

  const [topTab, setTopTab] = useState("historial");
  const [view, setView] = useState("table");
  const [employees, setEmployees] = useState([]);
  const [viewingEmployeeId, setViewingEmployeeId] = useState(null);
  const [refMonth, setRefMonth] = useState(() => firstOfMonth(`${new Date().getFullYear()}-${pad(new Date().getMonth() + 1)}-01`));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [correctionDay, setCorrectionDay] = useState(null);
  const [ttSettings, setTtSettings] = useState({ correction_mode: "requires_approval" });

  // Cargar lista de empleados.
  useEffect(() => {
    let cancelled = false;
    getEmployees()
      .then((r) => {
        if (cancelled) return;
        const list = unwrap(r) || [];
        setEmployees(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Cargar settings (correction_mode para el modal).
  useEffect(() => {
    let cancelled = false;
    getTimeTrackingSettings()
      .then((r) => {
        if (cancelled) return;
        const d = unwrap(r);
        if (d?.correction_mode) setTtSettings({ correction_mode: d.correction_mode });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Cargar jornadas del empleado seleccionado.
  const load = useCallback(async () => {
    if (!viewingEmployeeId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await getJornadaLaboral({
        view: "month",
        date: refMonth,
        employee_profile_id: viewingEmployeeId,
      });
      setData(unwrap(r));
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudo cargar el historial.");
    } finally {
      setLoading(false);
    }
  }, [refMonth, viewingEmployeeId]);

  useEffect(() => { load(); }, [load]);

  const tz = data?.timezone || DEFAULT_TZ;
  const todayYmd = useMemo(() => todayYmdInTz(tz), [tz]);

  const daysAll = useMemo(() => {
    if (!data?.days) return [];
    return data.days.map((d) => classifyDay(d, todayYmd, tz));
  }, [data, todayYmd, tz]);

  const totals = data?.totals || { worked_minutes: 0, planned_minutes: 0, balance_minutes: 0 };
  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === viewingEmployeeId) || null,
    [employees, viewingEmployeeId],
  );

  // Si el actor no tiene capability, la página no debería renderizarse.
  // Defensa en profundidad (la ruta también está gated en sidebar).
  if (!canApprove) {
    return (
      <div className="kr-page">
        <PageHeader eyebrow="RRHH" title="Jornadas" />
        <Card>
          <EmptyState
            icon="Lock"
            title="Sin acceso"
            description="Necesitas permisos de gestión de correcciones de jornada para esta sección."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="RRHH"
        title="Jornadas"
        description="Consulta el historial de jornadas de cualquier empleado y gestiona correcciones y cierre mensual."
      />

      <Tabs
        tabs={[
          { id: "historial", label: "Historial" },
          { id: "corrections", label: "Correcciones" },
          { id: "closures", label: "Cierre mensual" },
        ]}
        value={topTab}
        onChange={setTopTab}
      />

      {topTab === "corrections" ? (
        <BandejaCorrecciones
          isManager={true}
          currentUserId={membership.me?.id || null}
          currentEmployeeProfileId={membership.employeeProfileId}
          tz={tz}
        />
      ) : topTab === "closures" ? (
        <CierreMensualPanel />
      ) : (
        <>
          <Card padded={false}>
            <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <EmployeePicker
                value={viewingEmployeeId}
                onChange={setViewingEmployeeId}
                employees={employees}
              />
              <MonthPicker value={refMonth} onChange={setRefMonth} />
              <div style={{ flex: 1 }} />
              <ViewToggle view={view} setView={setView} />
            </div>

            {selectedEmployee && (
              <div style={{ borderTop: "1px solid var(--kr-border-subtle)", padding: "10px 16px", display: "flex", gap: 24, alignItems: "center", background: "var(--kr-neutral-50)", flexWrap: "wrap" }}>
                <SummaryStat k="Total trabajado" v={fmtDur(totals.worked_minutes) + " h"} />
                <SummaryStat k="Total esperado" v={fmtDur(totals.planned_minutes) + " h"} />
                <SummaryStat k="Diferencia" v={diffStr(totals.balance_minutes)} tone={totals.balance_minutes >= 0 ? "success" : "warning"} />
              </div>
            )}
          </Card>

          {error && <Alert tone="danger">{error}</Alert>}

          {!viewingEmployeeId ? (
            <Card>
              <EmptyState
                icon="UserSearch"
                title="Selecciona un empleado"
                description="Elige al empleado cuyo historial deseas revisar para corregir jornadas o aprobar solicitudes."
              />
            </Card>
          ) : loading ? (
            <Card>
              <div style={{ padding: 40, textAlign: "center" }}><Spinner size={18} /></div>
            </Card>
          ) : daysAll.length === 0 ? (
            <Card>
              <EmptyState
                icon="Calendar"
                title="Sin datos para este periodo"
                description="No hay jornadas registradas para el periodo seleccionado."
              />
            </Card>
          ) : view === "month" ? (
            <CalendarioMes data={daysAll} onOpenDay={setSelectedDay} />
          ) : (
            <TablaJornadas data={daysAll} onOpenDay={setSelectedDay} />
          )}
        </>
      )}

      <DetalleJornadaDrawer
        day={selectedDay}
        tz={tz}
        onClose={() => setSelectedDay(null)}
        onCorregir={(d) => setCorrectionDay(d)}
      />
      <CorregirJornadaModal
        open={!!correctionDay}
        day={correctionDay}
        tz={tz}
        correctionMode={ttSettings.correction_mode}
        targetEmployeeProfileId={viewingEmployeeId}
        isOnBehalf={viewingEmployeeId !== membership.employeeProfileId}
        onClose={() => setCorrectionDay(null)}
        onApplied={async () => {
          await load();
          setSelectedDay(null);
        }}
      />
    </div>
  );
}

function fullName(e) {
  return `${e?.first_name || ""} ${e?.last_name || ""}`.trim();
}

function EmployeePicker({ value, onChange, employees }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  const selected = useMemo(
    () => (value ? employees.find((e) => e.id === value) : null),
    [value, employees],
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((emp) => {
      const hay = [fullName(emp), emp.position, emp.email]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [employees, query]);

  // Cierra al hacer click fuera o pulsar Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Limpia búsqueda al cerrar.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const handlePick = (id) => {
    onChange(id);
    setOpen(false);
  };

  const triggerLabel = selected ? (fullName(selected) || selected.email || selected.id) : "Selecciona un empleado…";

  return (
    <div ref={rootRef} style={{ position: "relative", display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
        Empleado
      </span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          height: 36, minWidth: 280,
          border: "1px solid var(--kr-border)",
          borderRadius: 6, padding: "0 8px 0 10px",
          background: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 8,
          fontFamily: "var(--kr-font-sans)", fontSize: 13,
          color: "var(--kr-fg-strong)",
        }}
      >
        {selected ? (
          <EmployeeAvatar employeeId={selected.id} hasAvatar={!!selected.avatar_path} name={fullName(selected) || "?"} size={22} />
        ) : (
          <Icon name="UserSearch" size={14} style={{ color: "var(--kr-fg-muted)" }} />
        )}
        <div style={{
          flex: 1, textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          color: selected ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
        }}>
          {triggerLabel}
        </div>
        {selected && (
          <span
            role="button"
            tabIndex={0}
            aria-label="Quitar selección"
            onClick={(e) => { e.stopPropagation(); onChange(null); setOpen(false); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onChange(null); setOpen(false); } }}
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 20, height: 20, borderRadius: 999, color: "var(--kr-fg-muted)",
              cursor: "pointer",
            }}
          >
            <Icon name="X" size={12} />
          </span>
        )}
        <Icon name={open ? "ChevronUp" : "ChevronDown"} size={14} style={{ color: "var(--kr-fg-muted)" }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, marginTop: 4,
            width: "min(360px, 90vw)", zIndex: 50,
            border: "1px solid var(--kr-border)", borderRadius: 8,
            background: "#fff",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            padding: 10, display: "flex", flexDirection: "column", gap: 8,
          }}
        >
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Busca por nombre, cargo o email…"
            icon="Search"
            autoFocus
          />
          <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {filtered.length === 0 ? (
              <div style={{
                padding: "16px 8px", fontSize: 12, color: "var(--kr-fg-muted)", textAlign: "center",
              }}>
                <Icon name="UserX" size={16} style={{ display: "block", margin: "0 auto 6px", opacity: 0.6 }} />
                Sin coincidencias
              </div>
            ) : (
              filtered.map((emp) => {
                const isCurrent = emp.id === value;
                return (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => handlePick(emp.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px",
                      background: isCurrent ? "var(--kr-action-soft-bg)" : "transparent",
                      border: "none", borderRadius: 6,
                      cursor: "pointer", textAlign: "left", width: "100%",
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrent) e.currentTarget.style.background = "var(--kr-bg-subtle)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isCurrent ? "var(--kr-action-soft-bg)" : "transparent";
                    }}
                  >
                    <EmployeeAvatar employeeId={emp.id} hasAvatar={!!emp.avatar_path} name={fullName(emp) || "?"} size={32} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: "var(--kr-fg-strong)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {fullName(emp) || "(sin nombre)"}
                      </div>
                      <div style={{
                        fontSize: 11, color: "var(--kr-fg-muted)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {emp.position || emp.email || "—"}
                      </div>
                    </div>
                    {isCurrent && (
                      <Icon name="Check" size={14} style={{ color: "var(--kr-action)" }} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthPicker({ value, onChange }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 600, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Periodo</span>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <IconBtn icon="ChevronLeft" title="Mes anterior" size={28} onClick={() => onChange(addMonths(value, -1))} />
        <div style={{
          minWidth: 130, textAlign: "center", fontFamily: "var(--kr-font-sans)", fontSize: 13, fontWeight: 600,
          color: "var(--kr-fg-strong)", padding: "4px 8px", border: "1px solid var(--kr-border)", borderRadius: 6,
          background: "#fff",
        }}>
          {monthLabel(value)}
        </div>
        <IconBtn icon="ChevronRight" title="Mes siguiente" size={28} onClick={() => onChange(addMonths(value, 1))} />
      </div>
    </div>
  );
}

function ViewToggle({ view, setView }) {
  return (
    <div style={{ display: "flex", border: "1px solid var(--kr-border)", borderRadius: 6, overflow: "hidden", height: 32 }}>
      {[
        { id: "table", icon: "List", label: "Tabla" },
        { id: "month", icon: "Calendar", label: "Mes" },
      ].map((o) => (
        <button
          key={o.id}
          onClick={() => setView(o.id)}
          style={{
            padding: "0 12px",
            border: "none",
            borderLeft: o.id === "month" ? "1px solid var(--kr-border)" : "none",
            background: view === o.id ? "var(--kr-action-soft-bg)" : "#fff",
            color: view === o.id ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <Icon name={o.icon} size={12} />
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SummaryStat({ k, v, tone }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)" }[tone] || "var(--kr-fg-strong)";
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{k}</div>
      <div style={{ fontFamily: "var(--kr-font-sans)", fontSize: 14, color: c, fontWeight: 700, marginTop: 2 }}>{v}</div>
    </div>
  );
}

function TablaJornadas({ data, onOpenDay }) {
  return (
    <Card padded={false}>
      <div style={{
        display: "grid",
        gridTemplateColumns: "70px 90px 90px 90px 90px 110px 1fr",
        padding: "10px 20px", background: "var(--kr-neutral-50)",
        borderBottom: "1px solid var(--kr-border-subtle)",
        fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em",
        fontWeight: 600, color: "var(--kr-fg-muted)",
      }}>
        <div>Día</div><div>Entrada</div><div>Salida</div><div>Pausas</div>
        <div>Trabajado</div><div>Diferencia</div><div>Estado</div>
      </div>
      {data.map((d, i) => (
        <FilaJornada key={d.date} day={d} last={i === data.length - 1} onOpen={() => onOpenDay(d)} />
      ))}
    </Card>
  );
}

function FilaJornada({ day, last, onOpen }) {
  const [hover, setHover] = useState(false);
  const isInactive = day.isWeekend || day.noPlan || day.isFuture;
  const meta = day.status ? STATUS_META[day.status] : null;
  const clickable = !isInactive;
  const correctionsCount = day.corrections_count || 0;
  const pendingCount = day.pending_corrections_count || 0;

  let stateNode;
  if (day.isFuture) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>—</span>;
  else if (day.isWeekend) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>Fin de semana</span>;
  else if (day.noPlan) stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>No laborable</span>;
  else if (meta) stateNode = <Badge tone={meta.tone} dot>{meta.label}</Badge>;
  else stateNode = <span style={{ fontSize: 12, color: "var(--kr-fg-subtle)" }}>—</span>;

  return (
    <div
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onClick={clickable ? onOpen : undefined}
      style={{
        display: "grid",
        gridTemplateColumns: "70px 90px 90px 90px 90px 110px 1fr",
        padding: "11px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover && clickable ? "var(--kr-bg-subtle)" : "#fff",
        cursor: clickable ? "pointer" : "default",
        opacity: day.isFuture ? 0.55 : 1, alignItems: "center",
      }}
    >
      <div>
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", fontWeight: 600 }}>{day.weekday}</div>
        <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)" }}>{pad(day.dayNumber)}</div>
      </div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13 }}>{day.inStr}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13 }}>{day.outStr}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, color: "var(--kr-fg-muted)" }}>{day.pauseMin > 0 ? `${day.pauseMin} min` : "—"}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 600 }}>{day.worked_minutes > 0 ? fmtDur(day.worked_minutes) : "—"}</div>
      <div style={{
        fontFamily: "var(--kr-font-mono)", fontSize: 13, fontWeight: 600,
        color: day.planned_minutes === 0 || day.isFuture ? "var(--kr-fg-subtle)" : day.balance >= 0 ? "var(--kr-success-700)" : "var(--kr-warning-700)",
      }}>
        {day.planned_minutes === 0 || day.isFuture ? "—" : diffStr(day.balance)}
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
        {stateNode}
        {pendingCount > 0 && <Badge tone="warning" dot>En revisión</Badge>}
        {correctionsCount > 0 && <Badge tone="info" dot>Corregido{correctionsCount > 1 ? ` ×${correctionsCount}` : ""}</Badge>}
      </div>
    </div>
  );
}

function DetalleJornadaDrawer({ day, tz, onClose, onCorregir }) {
  const isOpen = !!day;
  const titleNode = day ? (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>Detalle de jornada</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 2 }}>
        {day.weekdayLong} · {day.dayNumber} {MONTH_NAMES[ymdToParts(day.date).m - 1].toLowerCase()} {ymdToParts(day.date).y}
      </div>
    </div>
  ) : null;

  return (
    <Drawer open={isOpen} onClose={onClose} title={titleNode} size="lg">
      {day && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
              <BigStat k="Entrada" v={day.inStr} />
              <BigStat k="Salida" v={day.outStr} />
              <BigStat k="Pausas" v={day.pauseMin > 0 ? `${day.pauseMin} min` : "—"} />
              <BigStat
                k="Trabajado neto"
                v={day.worked_minutes > 0 ? fmtDur(day.worked_minutes) + " h" : "—"}
                tone={day.balance < 0 ? "warning" : "success"}
              />
            </div>
          </Card>

          {(day.pending_corrections_count || 0) > 0 && (
            <Alert tone="warning">Hay una corrección pendiente de aprobación para esta jornada.</Alert>
          )}
          {(day.corrections_count || 0) > 0 && (
            <Alert tone="info">
              Esta jornada {day.corrections_count === 1 ? "fue corregida" : `fue corregida ${day.corrections_count} veces`}. Los marcajes mostrados reflejan la última corrección aplicada.
            </Alert>
          )}

          {!day.isFuture && (
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
              padding: "10px 14px", border: "1px dashed var(--kr-border)", borderRadius: 6,
              background: "var(--kr-bg-surface)",
            }}>
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", lineHeight: 1.4 }}>
                {day.is_closed ? (
                  <>
                    <strong>Mes cerrado.</strong> Esta jornada pertenece a un mes cerrado y no admite correcciones.
                    Reabre el mes desde la tab "Cierre mensual" si necesitas modificarla.
                  </>
                ) : (
                  <>Corregir la jornada del empleado quedará pendiente hasta que él la apruebe.</>
                )}
              </div>
              <Button
                variant="secondary" size="sm"
                icon={day.is_closed ? "Lock" : "Pencil"}
                onClick={() => !day.is_closed && onCorregir(day)}
                disabled={day.is_closed}
              >
                Corregir jornada
              </Button>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function BigStat({ k, v, tone }) {
  const c = { success: "var(--kr-success-700)", warning: "var(--kr-warning-700)" }[tone] || "var(--kr-fg-strong)";
  return (
    <div>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>{k}</div>
      <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 20, fontWeight: 700, color: c, marginTop: 4 }}>{v}</div>
    </div>
  );
}

export default RRHHJornadas;
